// #581 Task 9 — the confirm-and-import review data layer (React Query +
// realtime). Wraps the Task 6/7/8 backend routes:
//   GET    {boats|engines|trailers}/{id}/suggested-updates
//   POST   {boats|engines|trailers}/{id}/suggested-updates/apply
//   POST   {boats|engines|trailers}/{id}/suggested-updates/dismiss
//   POST   {boats|engines|trailers}/{id}/documents/analyze
// plus `useSuggestionsChannel`, the Centrifugo subscription that fires when a
// background extraction run lands new suggestions for the item (Task 5's
// SettleExtractionRun `suggestions_ready` broadcast).

import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useChannel } from '@/lib/realtime/useChannel';
import { useDealershipSettings } from '@/modules/crm/hooks/useDealershipSettings';
import { useCan } from '@/modules/tenant/hooks/useTenantAuth';
import { aiApi } from '../services/aiApi';
import { boatKeys } from './useBoats';
import { AI_PERMISSION_DENIED_STATUS } from './paidAiGuard';
import type {
  AnalyzeDocumentsInput,
  AnalyzeDocumentsResult,
  DecideSuggestionsInput,
  SuggestedUpdates,
  SuggestionItemType,
} from '../types/suggestedUpdates.types';

export const suggestedUpdatesKeys = {
  all: ['suggested-updates'] as const,
  item: (type: SuggestionItemType, id: string) => [...suggestedUpdatesKeys.all, type, id] as const,
};

/** The confirm-and-import review read. Disabled without an id (mirrors useBoat/useDocuments). */
export function useSuggestedUpdates(
  type: SuggestionItemType,
  id: string,
  opts?: { enabled?: boolean },
): UseQueryResult<SuggestedUpdates> {
  return useQuery({
    queryKey: suggestedUpdatesKeys.item(type, id),
    queryFn: () => aiApi.getSuggestedUpdates(type, id),
    enabled: (opts?.enabled ?? true) && !!id,
  });
}

/**
 * Apply the dealer's confirm-and-import selections. Invalidates BOTH this
 * item's suggested-updates cache (so applied rows drop off the review list)
 * AND the boat detail query (so the canvas/detail page re-fetches the newly
 * canonical field values) — per the Task 9 brief, unconditionally on `id`
 * regardless of `type` (an engine/trailer apply can still affect the parent
 * boat's linked-asset display).
 */
export function useApplySuggestedUpdates(type: SuggestionItemType, id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: DecideSuggestionsInput) => aiApi.applySuggestedUpdates(type, id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suggestedUpdatesKeys.item(type, id) });
      queryClient.invalidateQueries({ queryKey: boatKeys.detail(id) });
    },
  });
}

/**
 * Permanently dismiss suggestions the dealer declined. Only invalidates this
 * item's suggested-updates cache — dismissed rows never touch canonical
 * fields, so there is nothing on the boat detail query to refresh.
 */
export function useDismissSuggestedUpdates(type: SuggestionItemType, id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: DecideSuggestionsInput) => aiApi.dismissSuggestedUpdates(type, id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suggestedUpdatesKeys.item(type, id) });
    },
  });
}

/**
 * The manual "Analyze documents" / re-run trigger. Fire-and-forget (202 ACK) —
 * the terminal result arrives via `useSuggestionsChannel`'s
 * `suggestions_ready` event, not this mutation's response.
 */
export function useAnalyzeDocuments(type: SuggestionItemType, id: string) {
  // ACL-09 (#611, D-22) — document analysis is paid AI. Without
  // inventory.ai.generate it resolves locally to skipped_no_permission (#1458
  // WR-04: its own outcome, not the server's skipped_ai_disabled), so nothing
  // is sent.
  const canGenerate = useCan('inventory.ai.generate');

  return useMutation({
    mutationFn: (body: AnalyzeDocumentsInput): Promise<AnalyzeDocumentsResult> =>
      canGenerate
        ? aiApi.analyzeDocuments(type, id, body)
        : Promise.resolve({ status: AI_PERMISSION_DENIED_STATUS, media_count: 0 }),
  });
}

/** Narrow, defensive guard for the `suggestions_ready` event payload. */
function isSuggestionsReadyPayload(
  payload: Record<string, unknown>,
): payload is { type: 'suggestions_ready'; fields: number; entities: number } {
  return (
    payload.type === 'suggestions_ready' &&
    typeof payload.fields === 'number' &&
    typeof payload.entities === 'number'
  );
}

/**
 * Subscribes to the item's copilot channel (`tenant.{tenantId}.copilot.{itemId}`
 * — the same channel + `useChannel` hook `useFactoryPacketFill.ts` subscribes
 * on, see its `:269-272` channel-name construction). On a `suggestions_ready`
 * event: invalidate the item's cached suggested-updates (any type/id combo —
 * this hook is not itself type-scoped, see below) and call `onReady` with the
 * fresh counts so a caller can toast/badge without waiting on the refetch.
 *
 * Tenant-id sourcing: unlike `useFactoryPacketFill` (which takes `tenantId` as
 * a caller-supplied argument), this hook's public signature has no `tenantId`
 * param — it mirrors `useAIJobStatus`'s self-contained sourcing instead
 * (`useDealershipSettings().data?.tenant_id`), the established pattern for a
 * realtime hook that owns its own channel end-to-end.
 *
 * Invalidation is by a (type-agnostic) id match rather than
 * `suggestedUpdatesKeys.item(type, id)` directly: this hook only receives
 * `itemId`, not the item's type, so it invalidates every cached
 * `['suggested-updates', <any type>, itemId]` entry via a predicate — whichever
 * type is actually mounted on the current page is the one that gets refetched.
 */
export function useSuggestionsChannel(
  itemId: string,
  onReady?: (counts: { fields: number; entities: number }) => void,
): void {
  const queryClient = useQueryClient();
  const { data: settings } = useDealershipSettings();
  const tenantId = settings?.tenant_id ?? null;

  // Keep the latest callback in a ref so the channel subscription does not
  // re-bind when the caller passes a fresh inline closure each render
  // (mirrors useFactoryPacketFill's onDoneRef/onFailedRef idiom).
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const channel: string | null =
    tenantId && itemId ? `tenant.${tenantId}.copilot.${itemId}` : null;

  useChannel<Record<string, unknown>>(channel, {
    onPublication: (env) => {
      // ai-engine/Laravel publish raw at ctx.data OR wrap in {event, payload}
      // depending on the publisher — unwrap defensively (mirror useAIJobStatus
      // / useFactoryPacketFill).
      const raw = env as unknown as Record<string, unknown>;
      const payload: Record<string, unknown> =
        raw && typeof raw === 'object' && 'type' in raw
          ? raw
          : ((raw as { payload?: Record<string, unknown> })?.payload ?? {});

      if (!isSuggestionsReadyPayload(payload)) return;

      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === suggestedUpdatesKeys.all[0] && query.queryKey[2] === itemId,
      });

      onReadyRef.current?.({ fields: payload.fields, entities: payload.entities });
    },
  });
}
